import React, { createContext, useContext, useState, useEffect } from "react";

export type Theme =
  | "light" | "dark"
  | "slate" | "slate-dark"
  | "warm" | "warm-dark"
  | "forest" | "forest-dark"
  | "midnight" | "midnight-dark";

export interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

export const THEME_LABELS: Record<Theme, string> = {
  light: "Classic",
  dark: "Classic Dark",
  slate: "Slate",
  "slate-dark": "Slate Dark",
  warm: "Warm",
  "warm-dark": "Warm Dark",
  forest: "Forest",
  "forest-dark": "Forest Dark",
  midnight: "Midnight",
  "midnight-dark": "Midnight Dark",
};

const DARK_THEMES = new Set<Theme>(["dark", "slate-dark", "warm-dark", "forest-dark", "midnight-dark"]);

const VALID_THEMES = new Set<string>(["light","dark","slate","slate-dark","warm","warm-dark","forest","forest-dark","midnight","midnight-dark"]);

export const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem("cc-theme");
    if (stored && VALID_THEMES.has(stored)) return stored as Theme;
    return "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    if (DARK_THEMES.has(theme)) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  function setTheme(t: Theme) {
    localStorage.setItem("cc-theme", t);
    setThemeState(t);
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
